<script lang="ts">
  import { Link } from 'svelte-routing';
  import ProfileTab from './ProfileTab.svelte';
  import StatsTab from './StatsTab.svelte';
  import SettingsTab from './SettingsTab.svelte';
  import DonationsTab from './DonationsTab.svelte';
  import FollowsTab from './FollowsTab.svelte';
  import BouncingEmailBanner from '../../components/BouncingEmailBanner.svelte';
  import { withPageTitle } from '../../lib/page-title';
  import { DONATIONS_ENABLED } from '../../lib/features';
  import { user } from '../../lib/stores/auth';

  // Tabbed account page. Active tab is mirrored to ?tab= so refreshes
  // (and copy-pasted links) land back on the same panel. We read the
  // URL on mount and write back via history.replaceState whenever the
  // tab changes — no full nav, so the route doesn't re-render.

  type Tab = 'profile' | 'stats' | 'follows' | 'settings' | 'donations';

  // Slug stays `'profile'` (URL-stable for any existing bookmarks) but
  // the visible label is "Account" — that's what the popup menu in the
  // sidebar / top-nav points players to. The page heading is also
  // "Account", so the active-tab state reads as a section indicator
  // ("Account → Account") rather than a duplicate title.
  // Donations tab gated by the feature flag — tester accounts can't
  // donate yet (Stripe Cards pending approval). Re-enable by flipping
  // DONATIONS_ENABLED in lib/features.ts.
  const TABS: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Account' },
    { id: 'stats', label: 'Stats' },
    { id: 'follows', label: 'Follows' },
    { id: 'settings', label: 'Settings' },
    ...(DONATIONS_ENABLED ? [{ id: 'donations' as Tab, label: 'Donations' }] : []),
  ];

  function readInitialTab(): Tab {
    if (typeof window === 'undefined') return 'profile';
    const slug = new URL(window.location.href).searchParams.get('tab');
    return TABS.some((t) => t.id === slug) ? (slug as Tab) : 'profile';
  }

  let active = $state<Tab>(readInitialTab());

  // Title reflects the active tab so the browser-tab label tracks too.
  $effect(() => {
    const label = TABS.find((t) => t.id === active)?.label ?? 'Account';
    return withPageTitle(`Account — ${label}`);
  });

  // Mirror tab into the URL without adding history entries; the default
  // panel ("profile") is represented as a missing param to keep canonical
  // /account links clean.
  $effect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (active === 'profile') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', active);
    }
    window.history.replaceState({}, '', url.toString());
  });
</script>

<div class="account">
  <header class="account__header">
    <h1 class="account__title">Account</h1>
  </header>

  <BouncingEmailBanner onUpdateEmail={() => (active = 'profile')} />

  <div class="account__tab-row">
    <div class="account__tabs" role="tablist" aria-label="Account sections">
      {#each TABS as tab}
        <button
          type="button"
          role="tab"
          id="tab-{tab.id}"
          aria-controls="panel-{tab.id}"
          aria-selected={active === tab.id}
          class="account__tab"
          class:account__tab--active={active === tab.id}
          onclick={() => (active = tab.id)}
        >
          {tab.label}
        </button>
      {/each}
    </div>

    {#if $user}
      <!-- Right-aligned escape hatch to the public-facing profile page.
           Visually distinct from the tab buttons since `/u/{username}`
           is a separate route, not another tab on this page. -->
      <Link
        to={`/u/${$user.username}`}
        getProps={() => ({ class: 'account__view-profile' })}
      >
        View profile <span aria-hidden="true">›</span>
      </Link>
    {/if}
  </div>

  <div
    class="account__panel"
    role="tabpanel"
    id="panel-{active}"
    aria-labelledby="tab-{active}"
  >
    {#if active === 'profile'}
      <ProfileTab />
    {:else if active === 'stats'}
      <StatsTab />
    {:else if active === 'follows'}
      <FollowsTab />
    {:else if active === 'settings'}
      <SettingsTab />
    {:else if active === 'donations'}
      <DonationsTab />
    {/if}
  </div>
</div>
